import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Col, Row, UiBox, UiButton, UiQuestionnaireLayout, UiText} from '~/components/elements';
import {PAGE_URLS} from '~/constants';
import {PRODUCT_SKUS, WEIGHT_LOSS_KEYS, WEIGHT_LOSS_ONE_QUESTIONNAIRES} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {Option} from '~/types/models/questionnaire';

interface MealItem {
  key: string;
  name: string;
  quantity: number;
}

interface Props {}

const Questionnaire11NutritionScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.PLAN_WEEKLY_MENU);

  const collectedData = mdq.collectedData;

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.PLAN_WEEKLY_MENU]?.data,
  );

  const selectedPackage = collectedData[WEIGHT_LOSS_KEYS.PACKAGE_SELECTION] || {};
  const selectedProducts = selectedPackage?.data?.productSkus || [];

  const MAXIMUM_MEALS = 7;

  const {inputs, setInputs, validation} = useInput<{
    selectedMeals: MealItem[];
  }>({
    selectedMeals: currentAnswer?.selectedMeals || [],
  });

  //   const totalMealSelected = inputs.selectedMeals.reduce((acc, i) => acc + i.quantity, 0);

  const totalMealSelected = useMemo(() => {
    return inputs.selectedMeals.reduce((acc, i) => acc + i.quantity, 0);
  }, [inputs.selectedMeals]);

  const onClickMeal = (mealData: Option) => {
    if (totalMealSelected >= MAXIMUM_MEALS) {
      return;
    }

    const selectedMeal = inputs.selectedMeals.find(i => i.key === mealData.key);
    if (selectedMeal) {
      const updatedMeals = [...inputs.selectedMeals];
      for (let i = 0; i < updatedMeals.length; i++) {
        if (updatedMeals[i].key === mealData.key) {
          updatedMeals[i] = {...updatedMeals[i], quantity: updatedMeals[i].quantity + 1};
          break;
        }
      }
      setInputs({selectedMeals: updatedMeals});
      return;
    }
    const newMeal = {
      key: mealData.key,
      name: mealData.label,
      quantity: 1,
    };
    setInputs({selectedMeals: [...inputs.selectedMeals, newMeal]});
  };

  const onResetSelection = () => {
    setInputs({selectedMeals: []});
  };

  const onClickSubmit = () => {
    const answerData = {
      key: WEIGHT_LOSS_KEYS.PLAN_WEEKLY_MENU,
      index: questionnaire?.index || '',
      data: {
        selectedMeals: inputs.selectedMeals,
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));

    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.PLAN_WEEKLY_MENU]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.APPOINTMENT_PREFERENCES}));
    history.push(PAGE_URLS.MDQ1_NUTRITION_DELIVERY_PREFERENCES);
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title}
      disableNext={totalMealSelected < MAXIMUM_MEALS}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={1}>
        <UiText fontSize="xl" fontWeight="bold" mb={12}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" fontWeight="600" mb={4} maxW="80%">
          {questionnaire?.questions?.description}
        </UiText>
        <Col>
          <UiButton
            borderRadius={100}
            variant="primary"
            fontSize="xs"
            minH="32px"
            py={5}
            onClick={onResetSelection}
            _hover={{}}
            minW={32}
            alignSelf="center"
            disabled={inputs.selectedMeals.length === 0}
          >
            Reset Selection
          </UiButton>
          <UiText fontSize="0.625rem" fontWeight="700" mt={6} color="#AB0000">
            {MAXIMUM_MEALS - totalMealSelected} meal(s) remaining
          </UiText>
        </Col>
        <Col maxH={72} overflowY="scroll">
          <Row flexWrap="wrap" mb={8} mt={6}>
            {questionnaire?.options?.map((option, index) => {
              const selectedMeal = inputs.selectedMeals.find(i => i.key === option.key);
              return (
                <Col
                  key={option.key}
                  width="33%"
                  mb={6}
                  position="relative"
                  onClick={() => onClickMeal(option)}
                  _hover={{cursor: 'pointer'}}
                >
                  {selectedMeal && (
                    <Col
                      position="absolute"
                      bg="rgba(255, 255, 255, 0.8)"
                      justifyContent="center"
                      alignItems="center"
                      w="100%"
                      h="6rem"
                    >
                      <UiText fontSize="xl" fontWeight="700" mt={1}>
                        X{selectedMeal.quantity}
                      </UiText>
                    </Col>
                  )}

                  <img
                    src={option.image}
                    alt={option.label}
                    style={{width: '100%', height: '6rem', maxWidth: 'none', objectFit: 'contain'}}
                  />
                  <UiText fontSize="0.625rem" fontWeight="500" mt={1}>
                    {option.label}
                  </UiText>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire11NutritionScreen;
